/**
 * Host services for the Plugin Content Pack client-side entity (ADR-021).
 *
 * The executor (`@fias/client-entities` → plugin-content) runs in this page
 * and needs a credential to read a plugin's pack from the content CDN. This
 * fetches it from the platform — `GET /v1/arches/:id/content-access`, with the
 * viewer's session cookie, under the same access rules as the plugin bundle —
 * and maps failures onto the executor's typed error codes. The credential
 * stays in this page; the plugin iframe never sees it.
 */

import type { PluginContentAccessResult, PluginContentHost } from '@fias/client-entities';
import { API_BASE } from '@/lib/api-base';
import { sessionCredentials } from '@/lib/embed-document';

function unavailable(message: string): PluginContentAccessResult {
  return { ok: false, error: { code: 'CONTENT_UNAVAILABLE', message } };
}

export async function fetchPluginContentAccess(
  archeId: string,
): Promise<PluginContentAccessResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/arches/${encodeURIComponent(archeId)}/content-access`, {
      credentials: sessionCredentials(),
    });
  } catch {
    return unavailable('the content could not be reached');
  }

  const body = (await response.json().catch(() => null)) as {
    baseUrl?: unknown;
    manifestHash?: unknown;
    signedQuery?: unknown;
    expiresAt?: unknown;
    error?: { code?: string; message?: string };
  } | null;

  if (response.ok) {
    if (
      typeof body?.baseUrl === 'string' &&
      typeof body.manifestHash === 'string' &&
      typeof body.signedQuery === 'string' &&
      typeof body.expiresAt === 'string'
    ) {
      return {
        ok: true,
        access: {
          baseUrl: body.baseUrl,
          manifestHash: body.manifestHash,
          signedQuery: body.signedQuery,
          expiresAt: body.expiresAt,
        },
      };
    }
    return unavailable('the content credential was malformed');
  }
  if (body?.error?.code === 'CONTENT_NOT_FOUND') {
    return {
      ok: false,
      error: { code: 'CONTENT_NOT_FOUND', message: 'this plugin has no content pack' },
    };
  }
  return unavailable(body?.error?.message ?? `the content is unavailable (${response.status})`);
}

export const pluginContentHost: PluginContentHost = {
  fetchAccess: fetchPluginContentAccess,
  environment: (import.meta.env.VITE_ENVIRONMENT as string | undefined) || 'development',
};
